import Box from '@mui/material/Box';
import Alert from '@mui/material/Alert';
import { useTheme } from '@mui/material/styles';
import { useNavigate } from 'react-router';
import { Typography } from '@mui/material';
import IconButton, { iconButtonClasses } from '@mui/material/IconButton';

import { Iconify } from 'src/components/iconify';
import { useBoolean } from 'src/hooks/use-boolean';
import { _contacts, _notifications } from 'src/_mock';

import { useSettingsContext } from 'src/components/settings';
import { useActiveScreen, usePathname } from 'src/routes/hooks';
import { useAuthContext } from 'src/auth/hooks';

import { Main } from './main';
import { NavMobile } from './nav-mobile';
import { layoutClasses } from '../classes';
import { _account } from '../config-nav-account';
import { _workspaces } from '../config-nav-workspace';
import { MenuButton } from '../components/menu-button';
import { LayoutSection } from '../core/layout-section';
import { HeaderSection } from '../core/header-section';
import { useNavColorVars } from './styles';
import { AccountDrawer } from '../components/account-drawer';
import { navData as dashboardNavData, superAdminData } from '../config-nav-dashboard';
import { fDate } from 'src/utils/format-time';
import { ReferralRewardsPromo } from 'src/sections/account/view/referral-rewards-promo';

// ----------------------------------------------------------------------

export function DashboardLayout({ sx, children, header, data }) {
  const { user, checkUserSession } = useAuthContext();
  const theme = useTheme();
  const navigate = useNavigate();
  const mobileNavOpen = useBoolean();

  const settings = useSettingsContext();

  const navColorVars = useNavColorVars(theme, settings);

  const layoutQuery = 'lg';

  const navData = data?.nav ?? dashboardNavData;

  const isNavMini = settings.navLayout === 'mini';
  const isNavHorizontal = settings.navLayout === 'horizontal';
  const isNavVertical = isNavMini || settings.navLayout === 'vertical';

  const currentPath = useActiveScreen(usePathname(), false);

  const handleNavigateHome = () => {
    if (user?.role?.id === 3 && user?.gym?.status === 'inactive') {
      console.log('home');
    } else {
      navigate('/dashboard/home');
      localStorage.removeItem('activeTab');
      window.location.reload();
    }
  };

  const handleNavigateChat = async () => {
    if (currentPath !== 'chat') {
      navigate('/dashboard/chat');
    }
    await checkUserSession();
  };

  return (
    <LayoutSection
      /** **************************************
       * Header
       *************************************** */
      headerSection={
        <HeaderSection
          layoutQuery={layoutQuery}
          disableElevation={isNavVertical}
          slotProps={{
            toolbar: {
              sx: {
                ...(isNavHorizontal && {
                  bgcolor: 'var(--layout-nav-bg)',
                  [`& .${iconButtonClasses.root}`]: {
                    color: 'var(--layout-nav-text-secondary-color)',
                  },
                  [theme.breakpoints.up(layoutQuery)]: {
                    height: 'var(--layout-nav-horizontal-height)',
                  },
                }),
              },
            },
            container: {
              maxWidth: false,
              sx: {
                ...(isNavVertical && { px: { [layoutQuery]: 5 } }),
              },
            },
          }}
          sx={header?.sx}
          slots={{
            topArea: (
              <>
                {user?.is_whitelisted === 1 ? null : (
                  <>
                    {user?.gym?.owner?.id === user?.id && (
                      <Alert
                        severity="error"
                        sx={{
                          display:
                            (user?.gym?.subscription?.type === 'paid' &&
                              user?.gym?.subscription?.days_remaining > 4) ||
                            user?.gym?.status === 'pending' ||
                            user?.gym?.status === 'under_review' ||
                            !user?.gym?.subscription
                              ? 'none'
                              : 'flex',
                          borderRadius: 0,
                        }}
                      >
                        {user?.gym?.subscription?.type === 'trail' &&
                        user?.gym?.subscription?.days_remaining !== 0
                          ? `Your free trial period is ending on (${fDate(user?.gym?.subscription?.end_date)}). Subscribe now to continue our services.`
                          : user?.gym?.subscription?.type === 'paid' &&
                              user?.gym?.subscription?.days_remaining <= 3
                            ? `Your subscription period is ending on (${fDate(user?.gym?.subscription?.end_date)}). Subscribe again to continue our services.`
                            : user?.gym?.subscription?.type === 'canceled'
                              ? 'Your subscription has been canceled. To continue our services, please resubscribe.'
                              : user?.gym?.subscription?.type === 'expired'
                                ? user?.gym?.subscription?.amount === 0
                                  ? `Your free trial period has ended on (${fDate(user?.gym?.subscription?.end_date)}). Subscribe now to continue our services.`
                                  : user?.gym?.subscription?.days_remaining === 0
                                    ? `Your subscription has expired on (${fDate(user?.gym?.subscription?.end_date)}). Subscribe now to regain access to our services.`
                                    : `Your subscription is expiring on (${fDate(user?.gym?.subscription?.end_date)}). Renew now to regain access to our services.`
                                : ''}
                      </Alert>
                    )}

                    {user?.member_subscription && (
                      <Alert
                        variant="filled"
                        severity={
                          user?.member_subscription?.status === 'past_due' ? 'warning' : 'error'
                        }
                        sx={{
                          display:
                            // HIDE alert conditions
                            (user?.member_subscription?.status === 'active' &&
                              user?.member_subscription?.days_remaining > 4) ||
                            (user?.member_subscription?.status === 'trialing' &&
                              user?.member_subscription?.days_remaining > 4) ||
                            !user?.member_subscription?.status
                              ? 'none'
                              : 'flex',
                          borderRadius: 0,
                          // mb: 2,
                        }}
                      >
                        <Typography variant="button" color="inherit">
                          {/* Trial ended but not subscribed */}
                          {user?.member_subscription?.status === 'canceled' &&
                          user?.member_subscription?.amount === null
                            ? `Your free trial ended on (${fDate(user?.member_subscription?.ended_at || user?.member_subscription?.current_period_end)}). Subscribe now to continue accessing premium features.`
                            : // Trial ending soon
                              user?.member_subscription?.status === 'trialing' &&
                                user?.member_subscription?.days_remaining <= 3 &&
                                user?.member_subscription?.days_remaining > 0
                              ? `Your free trial ends in ${user?.member_subscription?.days_remaining} ${user?.member_subscription?.days_remaining === 1 ? 'day' : 'days'} (${fDate(user?.member_subscription?.current_period_end)}). Subscribe to keep your premium features.`
                              : // Active subscription ending soon
                                user?.member_subscription?.status === 'active' &&
                                  user?.member_subscription?.days_remaining <= 3
                                ? `Your subscription renews in ${user?.member_subscription?.days_remaining} ${user?.member_subscription?.days_remaining === 1 ? 'day' : 'days'} (${fDate(user?.member_subscription?.current_period_end)}).`
                                : // Past due payment
                                  user?.member_subscription?.status === 'past_due'
                                  ? `Your last payment failed. Please update your payment method to continue your subscription.`
                                  : // Canceled subscription
                                    user?.member_subscription?.status === 'canceled'
                                    ? user?.member_subscription?.amount > 0
                                      ? user?.member_subscription?.days_remaining > 0
                                        ? `Your subscription was canceled and will end on (${fDate(user?.member_subscription?.current_period_end)}). Resubscribe to continue.`
                                        : `Your subscription was ended on (${fDate(user?.member_subscription?.current_period_end)}). Subscribe again to continue.`
                                      : `Your free trial was canceled. You can still subscribe to get premium features.`
                                    : // Default empty
                                      ''}
                        </Typography>
                      </Alert>
                    )}
                  </>
                )}
              </>
            ),
            bottomArea: null,
            leftArea: (
              <>
                {user?.role?.id === 1 && (
                  <>
                    {/* -- Nav mobile -- */}
                    <MenuButton
                      onClick={mobileNavOpen.onTrue}
                      sx={{
                        mr: 1,
                        ml: -1,
                        // [theme.breakpoints.up(layoutQuery)]: { display: 'none' },
                      }}
                    />
                    <NavMobile
                      data={user?.role?.id === 1 ? superAdminData : navData}
                      open={mobileNavOpen.value}
                      onClose={mobileNavOpen.onFalse}
                      cssVars={navColorVars.section}
                    />
                  </>
                )}

                {/* {currentPath !== 'home' && user?.role?.id !== 1 && (
                  <Button
                    // component={RouterLink}
                    // href="/dashboard/home"
                    onClick={() => {
                      navigate(-1);
                    }}
                    startIcon={<Iconify icon="eva:arrow-ios-back-fill" width={16} />}
                    sx={{ mr: 1.5 }}
                  >
                    Back
                  </Button>
                )} */}
                <Typography
                  onClick={() => handleNavigateHome()}
                  sx={{
                    fontWeight: 'bold',
                    fontSize: { xs: '1rem', sm: '1.5rem' },
                    cursor:
                      user?.role?.id === 3 && user?.gym?.status === 'inactive'
                        ? 'default'
                        : 'pointer',
                  }}
                  color="primary"
                >
                  FITNEX
                </Typography>
              </>
            ),
            rightArea: (
              <Box display="flex" alignItems="center" gap={{ xs: 0, sm: 0.8 }}>
                {/* CHAT BUTTON */}

                {user?.role?.id === 6 ||
                (user?.role?.id === 3 && user?.gym?.status === 'inactive') ? (
                  <></>
                ) : (
                  <IconButton onClick={handleNavigateChat}>
                    <Iconify
                      width={28}
                      icon="gridicons:chat"
                      sx={{
                        color:
                          currentPath === 'chat'
                            ? theme.palette.primary.main
                            : theme.palette.text.disabled,
                      }}
                    />
                  </IconButton>
                )}
                {/* -- Searchbar -- */}
                {/* <Searchbar data={navData} /> */}
                {/* -- Language popover -- */}
                {/* <LanguagePopover data={allLangs} /> */}
                {/* -- Notifications popover -- */}
                {/* <NotificationsDrawer sx={{ mr: 1 }} data={_notifications} /> */}
                {/* -- Contacts popover -- */}
                {/* <ContactsPopover data={_contacts} /> */}
                {/* -- Settings button -- */}
                {/* <SettingsButton /> */}
                {/* -- Account drawer -- */}
                <AccountDrawer data={_account} />
              </Box>
            ),
          }}
        />
      }
      /** **************************************
       * Sidebar
       *************************************** */
      sidebarSection={
        isNavHorizontal ? null : null
        // (
        //   <NavVertical
        //     data={navData}
        //     isNavMini={isNavMini}
        //     layoutQuery={layoutQuery}
        //     cssVars={navColorVars.section}
        //     onToggleNav={() =>
        //       settings.onUpdateField(
        //         'navLayout',
        //         settings.navLayout === 'vertical' ? 'mini' : 'vertical'
        //       )
        //     }
        //   />
        // )
      }
      /** **************************************
       * Footer
       *************************************** */
      footerSection={null}
      /** **************************************
       * Style
       *************************************** */
      cssVars={{
        ...navColorVars.layout,
        '--layout-transition-easing': 'linear',
        '--layout-transition-duration': '120ms',
        '--layout-nav-mini-width': '88px',
        '--layout-nav-vertical-width': '300px',
        '--layout-nav-horizontal-height': '64px',
        '--layout-dashboard-content-pt': theme.spacing(1),
        '--layout-dashboard-content-pb': theme.spacing(8),
        '--layout-dashboard-content-px': theme.spacing(5),
      }}
      sx={{
        [`& .${layoutClasses.hasSidebar}`]: {
          [theme.breakpoints.up(layoutQuery)]: {
            transition: theme.transitions.create(['padding-left'], {
              easing: 'var(--layout-transition-easing)',
              duration: 'var(--layout-transition-duration)',
            }),
            pl: isNavMini ? 'var(--layout-nav-mini-width)' : 'var(--layout-nav-vertical-width)',
          },
        },
        ...sx,
      }}
    >
      <Main isNavHorizontal={isNavHorizontal}>{children}</Main>
      <ReferralRewardsPromo user={user} />
    </LayoutSection>
  );
}
